import { IconType } from 'react-icons';
import {
  LuBookOpen,
  LuCalendar,
  LuCalendarPlus,
  LuClipboardList,
  LuClock,
  LuContact,
  LuDoorOpen,
  LuFileSpreadsheet,
  LuFileText,
  LuGavel,
  LuLayoutDashboard,
  LuLogIn,
  LuPlus,
  LuScale,
  LuSearch,
  LuSettings,
  LuTimer,
  LuTrendingUp,
  LuUserCog,
  LuUsers,
  LuWallet,
} from 'react-icons/lu';

export interface MenuAccess {
  isAdmin: boolean;
  reportIV: boolean;
  reportV: boolean;
  showAuditLogins: boolean;
}

type Label = string | ((access: MenuAccess) => string);
type Rule = (access: MenuAccess) => boolean;

interface LeafDefinition {
  label: Label;
  to: string;
  icon?: IconType;
  visible?: Rule;
}

interface GroupDefinition {
  id: string;
  label: string;
  icon: IconType;
  items: LeafDefinition[];
}

interface LinkDefinition extends LeafDefinition {
  id: string;
  icon: IconType;
}

type EntryDefinition = GroupDefinition | LinkDefinition;

interface SectionDefinition {
  title: string;
  entries: EntryDefinition[];
}

export interface MenuLeaf {
  label: string;
  to: string;
  icon?: IconType;
}

export interface MenuGroup {
  id: string;
  label: string;
  icon: IconType;
  items: MenuLeaf[];
}

export interface MenuLink extends MenuLeaf {
  id: string;
  icon: IconType;
}

export type MenuEntry = MenuGroup | MenuLink;

export interface MenuSection {
  title: string;
  entries: MenuEntry[];
}

export const AUDIT_LOG_VIEWERS = 'f18f33d1-3c01-4822-9238-6f5db10acbfb,b94eef40-1f25-4e21-bb29-909a6b7c28eb,6d21f6a6-c8c2-4605-89e1-b6e0604d5bce';

const admin: Rule = (access) => access.isAdmin;

export const isGroup = (entry: MenuEntry): entry is MenuGroup => 'items' in entry;

export const brandIcon = LuScale;

const definition: SectionDefinition[] = [
  {
    title: 'Dia a dia',
    entries: [
      {
        id: 'processos',
        label: 'Processos',
        icon: LuGavel,
        items: [
          { label: 'Consultar processos', to: '/lawsuit/main/search', icon: LuSearch },
          { label: 'Consultar processos com movimentação por período', to: '/lawsuit/hasprogress/search', icon: LuSearch },
          { label: 'Novo processo', to: '/lawsuit/main/create', icon: LuPlus },
          { label: 'Listar todos processos', to: '/lawsuit/main', icon: LuGavel },
        ],
      },
      {
        id: 'agenda',
        label: 'Agenda',
        icon: LuCalendar,
        items: [
          { label: 'Consultar agendamentos', to: '/schedule/search', icon: LuSearch },
          { label: 'Novo agendamento', to: '/schedule/create', icon: LuCalendarPlus },
          {
            label: (access) => (access.isAdmin ? 'Listar agendamentos' : 'Listar meus agendamentos'),
            to: '/schedule/myschedule',
            icon: LuCalendar,
          },
          { label: 'Gerenciar locais/salas', to: '/schedule/meeting/room', icon: LuDoorOpen },
        ],
      },
      {
        id: 'horas',
        label: 'Horas',
        icon: LuClock,
        items: [
          { label: 'Cadastrar hora', to: '/management/workinghour/create', icon: LuPlus },
          {
            label: (access) => (access.isAdmin ? 'Consultar horas' : 'Consultar minhas horas'),
            to: '/management/workinghour/search',
            icon: LuSearch,
          },
          { label: 'Listar horas pendentes de finalização', to: '/management/workinghour/pendingconclusion', icon: LuSearch },
          { label: 'Referência de horas', to: '/lawsuit/reference', icon: LuTimer },
        ],
      },
    ],
  },
  {
    title: 'Cadastros',
    entries: [
      {
        id: 'apoio',
        label: 'Apoio Processos',
        icon: LuBookOpen,
        items: [
          { label: 'Descrição do pedido', to: '/lawsuit/legalrequest' },
          { label: 'Natureza de custos e depósitos', to: '/lawsuit/costdeposit' },
          { label: 'Natureza da intervenção', to: '/lawsuit/intervention' },
          { label: 'Natureza do processo', to: '/lawsuit/naturesuit' },
          { label: 'Órgão julgador', to: '/lawsuit/judgingbody' },
          { label: 'Procedimento', to: '/lawsuit/procedure' },
          { label: 'Resultado', to: '/lawsuit/result' },
          { label: 'Tipo de ação', to: '/lawsuit/suittype' },
          { label: 'Tipo de processo vinculado', to: '/lawsuit/linkedlawsuittype' },
        ],
      },
      {
        id: 'gestao',
        label: 'Gestão',
        icon: LuUserCog,
        items: [
          { label: 'Advogados', to: '/management/attorney', icon: LuContact, visible: admin },
          { label: 'Administradores', to: '/management/administrator', icon: LuContact, visible: admin },
          { label: 'Assistentes / estagiários', to: '/management/assistant', icon: LuContact, visible: admin },
          { label: 'Clientes', to: '/customer', icon: LuContact },
          { label: 'Grupo econômico', to: '/businessgroup', icon: LuContact, visible: admin },
          { label: 'Grupos', to: '/management/group', icon: LuUsers, visible: admin },
        ],
      },
      {
        id: 'financeiro',
        label: 'Financeiro',
        icon: LuWallet,
        items: [
          { label: 'Índices financeiros', to: '/finance/financialindex', icon: LuTrendingUp },
          { label: 'Indexador', to: '/finance/indexer', icon: LuTrendingUp },
        ],
      },
    ],
  },
  {
    title: 'Análise',
    entries: [
      {
        id: 'relatorios',
        label: 'Relatórios',
        icon: LuClipboardList,
        items: [
          { label: 'Config. Relatório horas e-mail', to: '/management/reporthouremail/edit', icon: LuSettings },
          { label: 'Modelo I (Resumido)', to: '/report/lawsuit/summary/search', icon: LuFileText },
          {
            label: 'Modelo I (Grupo econômico customizável)',
            to: '/report/lawsuit/summary/customizable/search',
            icon: LuFileText,
            visible: admin,
          },
          { label: 'Modelo II (Financeiro)', to: '/report/financialinformation/search', icon: LuFileText },
          { label: 'Modelo III (Andamentos)', to: '/report/financialinformationmodIII/search', icon: LuFileText },
          {
            label: 'Modelo III (Seleção)',
            to: '/report/financialinformationmodIIIselect/search',
            icon: LuFileText,
            visible: admin,
          },
          {
            label: 'Modelo IV (Horas)',
            to: '/report/hour/general/search',
            icon: LuFileText,
            visible: (access) => access.reportIV,
          },
          {
            label: 'Modelo V (Horas por Cliente)',
            to: '/report/hour/customer/search',
            icon: LuFileText,
            visible: (access) => access.reportV,
          },
          { label: 'Modelo VI (Horas Faturadas)', to: '/report/hour/billed/search', icon: LuFileText, visible: admin },
          {
            label: 'Modelo VII (Horas por Profissional)',
            to: '/report/hour/attorney/analytical/search',
            icon: LuFileText,
            visible: admin,
          },
          {
            label: 'Modelo VIII (Horas Totais por Profissionais)',
            to: '/report/hour/attorney/simplified/search',
            icon: LuFileText,
            visible: admin,
          },
          { label: 'Modelo IX (Percentuais de Horas)', to: '/report/hour/percentage/search', icon: LuFileText, visible: admin },
          { label: 'Exportar para Excel', to: '/report/lawsuit/simplified/excel/search', icon: LuFileSpreadsheet },
          { label: 'Exportar para Excel (Soma processos por setor)', to: '/report/lawsuit/grouped/excel/search', icon: LuFileSpreadsheet },
        ],
      },
      {
        id: 'gerencial',
        label: 'Gerencial',
        icon: LuLayoutDashboard,
        to: '/management/search',
        visible: admin,
      },
      {
        id: 'auditoria',
        label: 'Auditoria logins',
        icon: LuLogIn,
        to: '/audit/logins',
        visible: (access) => access.showAuditLogins,
      },
    ],
  },
];

const isAllowed = (rule: Rule | undefined, access: MenuAccess) => !rule || rule(access);

const resolveLabel = (label: Label, access: MenuAccess) => (typeof label === 'function' ? label(access) : label);

const resolveLeaf = (leaf: LeafDefinition, access: MenuAccess): MenuLeaf => ({
  label: resolveLabel(leaf.label, access),
  to: leaf.to,
  icon: leaf.icon,
});

const resolveEntry = (entry: EntryDefinition, access: MenuAccess): MenuEntry | null => {
  if (!isAllowed((entry as LeafDefinition).visible, access)) return null;

  if ('items' in entry) {
    const items = entry.items
      .filter((leaf) => isAllowed(leaf.visible, access))
      .map((leaf) => resolveLeaf(leaf, access));
    return items.length ? { id: entry.id, label: entry.label, icon: entry.icon, items } : null;
  }

  return { ...resolveLeaf(entry, access), id: entry.id, icon: entry.icon };
};

export const buildMenu = (access: MenuAccess): MenuSection[] => definition
  .map((section) => ({
    title: section.title,
    entries: section.entries
      .map((entry) => resolveEntry(entry, access))
      .filter((entry): entry is MenuEntry => entry !== null),
  }))
  .filter((section) => section.entries.length > 0);

const parseJson = (raw: string | null) => {
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const readMenuAccess = (storage: Pick<Storage, 'getItem'>): MenuAccess => {
  const userData = parseJson(storage.getItem('@Jurisnet:user'));
  const userId: string | undefined = userData?.id;

  const access = parseJson(storage.getItem('@Jurisnet:access')) || parseJson(userData?.access_info ?? null);

  const allowedIds = AUDIT_LOG_VIEWERS.split(',').map((id) => id.trim()).filter(Boolean);

  return {
    isAdmin: access ? Number(access.menu) === 1 : false,
    reportIV: access ? Number(access.permissaoiv) === 1 : false,
    reportV: access ? Number(access.permissaov) === 1 : false,
    showAuditLogins: Boolean(userId) || allowedIds.includes(userId as string),
  };
};

export const findGroupId = (sections: MenuSection[], pathname: string): string | null => {
  const matches = (to: string) => pathname === to || pathname.startsWith(`${to}/`);

  const groups = sections.flatMap((section) => section.entries).filter(isGroup);
  const group = groups.find((item) => item.items.some((leaf) => matches(leaf.to)));
  return group ? group.id : null;
};
